import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { devLog } from "../../../utils/devLogger";
import CustomTiptapEditor from "../../../components/Editor/CustomTiptapEditor";
import { getTermsList, createTerms } from "../../../lib/api/admin/adminApi";
import { toast } from "react-toastify";

type TabType = "이용약관(KR)" | "이용약관(EN)" | "개인정보 취급방침(KR)" | "개인정보 취급방침(EN)" | "사업자 정보(KR)" | "사업자 정보(EN)";

const TAB_INDEX_MAP: Record<TabType, number> = {
  "이용약관(KR)": 1,
  "이용약관(EN)": 2,
  "개인정보 취급방침(KR)": 3,
  "개인정보 취급방침(EN)": 4,
  "사업자 정보(KR)": 5,
  "사업자 정보(EN)": 6,
};

interface TermsData {
  index: number;
  content: string;
  termLang: 'KOR' | 'ENG';
  createId: string;
  createAt: string;
  updateAt: string | null;
  admin: {
    name: string;
    adminId: string;
  };
}

const TermsPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<TabType>("이용약관(KR)");
  const [content, setContent] = useState<string>("");
  const [termsData, setTermsData] = useState<TermsData[]>([]);
  const [selectedTermsIndex, setSelectedTermsIndex] = useState<number | null>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true); // 초기 로딩 상태를 true로 설정
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true); // 초기 로딩 완료 여부

  // 약관 저장
  const handleSave = async () => {
    if (!selectedTermsIndex) {
      toast.error("선택된 약관이 없습니다.");
      return;
    }

    if (!content || content.trim() === "") {
      toast.error("약관 내용을 입력해주세요.");
      return;
    }

    try {
      setIsLoading(true);
      
      // 현재 선택된 탭의 언어 확인
      const currentTab = selectedTab;
      const termLang = currentTab.includes("(KR)") ? "KOR" : "ENG";
      
      devLog("약관 저장:", { 
        selectedTermsIndex, 
        content: content.substring(0, 100) + "...", // 내용이 길 수 있으므로 일부만 로그
        termLang,
        selectedTab 
      });
      
      const response = await createTerms(selectedTermsIndex, {
        content: content,
        termLang: termLang
      });
      
      // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
      if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
        devLog("약관 저장 성공:", response);
        toast.success("저장되었습니다!");
        
        // 저장 후 데이터 새로고침 (단순 데이터만 업데이트)
        const refreshResponse = await getTermsList();
        if (refreshResponse && refreshResponse.data && refreshResponse.data.data && Array.isArray(refreshResponse.data.data)) {
          setTermsData(refreshResponse.data.data);
          devLog("저장 후 데이터 새로고침 완료:", refreshResponse.data.data);
        }
      } else {
        // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
        const errorMessage = response?.data?.error?.customMessage || 
                            response?.data?.message || 
                            '저장 처리 중 오류가 발생했습니다.';
        devLog("약관 저장 실패 (API 응답):", response);
        toast.error(`저장에 실패했습니다. (${errorMessage})`);
      }
      
    } catch (err: any) {
      devLog("약관 저장 실패:", err);
      
      // callApi에서 던진 에러 처리
      const errorMessage = err?.data?.error?.customMessage || 
                          err?.data?.message ||
                          err?.response?.data?.error?.customMessage || 
                          err?.response?.data?.message || 
                          err?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      toast.error(`저장에 실패했습니다. (${errorMessage})`);
    } finally {
      setIsLoading(false);
    }
  };  // 탭 클릭 핸들러
  const handleTabClick = (tab: TabType) => {
    devLog("탭 클릭:", tab);
    devLog("현재 termsData:", termsData);
    devLog("termsData 타입:", typeof termsData);
    devLog("termsData Array.isArray:", Array.isArray(termsData));
    
    setSelectedTab(tab);
    const tabIndex = TAB_INDEX_MAP[tab];
    devLog("선택된 탭 인덱스:", tabIndex);

    // termsData가 배열인지 확인
    if (!Array.isArray(termsData)) {
      devLog("termsData가 배열이 아님:", termsData);
      setContent("");
      setSelectedTermsIndex(tabIndex);
      return;
    }

    const selectedTerm = termsData.find((term) => Number(term.index) === tabIndex);
    devLog("선택된 약관:", selectedTerm);

    if (selectedTerm) {
      devLog("약관 내용 설정:", selectedTerm.content);
      setContent(selectedTerm.content);
      setSelectedTermsIndex(Number(selectedTerm.index));
    } else {
      // 해당 인덱스의 약관이 없으면 빈 내용으로 설정
      devLog("해당 인덱스의 약관이 없음, 빈 내용으로 설정");
      setContent("");
      setSelectedTermsIndex(tabIndex);
    }
  };

  // 에디터 내용 업데이트 핸들러
  const handleEditorUpdate = (newContent: string) => {
    setContent(newContent);
  };

  // 컴포넌트 마운트 시 약관 데이터 조회
  useEffect(() => {
    devLog("컴포넌트 마운트 - 약관 데이터 조회");
    
    const initializeData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        devLog("약관 데이터 조회 시작");
        
        const response = await getTermsList();
        devLog("약관 데이터 조회 응답:", response);
        
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          setTermsData(response.data.data);
          devLog("약관 데이터 설정 완료:", response.data.data);
          
          // 데이터 로딩 완료 후 즉시 현재 탭의 컨텐츠 설정
          const tabIndex = TAB_INDEX_MAP[selectedTab];
          const selectedTerm = response.data.data.find((term: TermsData) => Number(term.index) === tabIndex);
          if (selectedTerm) {
            devLog("초기 로딩 완료 - 약관 내용 설정:", selectedTerm.content);
            setContent(selectedTerm.content);
            setSelectedTermsIndex(selectedTerm.index);
          } else {
            devLog("초기 로딩 완료 - 해당 인덱스의 약관이 없음");
            setContent("");
            setSelectedTermsIndex(tabIndex);
          }
        } else {
          devLog("응답 데이터가 배열이 아님:", response);
          setTermsData([]);
          setContent("");
          setSelectedTermsIndex(TAB_INDEX_MAP[selectedTab]);
        }
        
        setIsInitialLoad(false);
      } catch (err) {
        devLog("약관 데이터 조회 실패:", err);
        setError("약관 데이터를 불러오는데 실패했습니다.");
        setTermsData([]);
        setContent("");
        setSelectedTermsIndex(TAB_INDEX_MAP[selectedTab]);
        setIsInitialLoad(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeData();
  }, [selectedTab]); // selectedTab 의존성 추가

  // 탭 변경 시 약관 내용 업데이트 (저장 후 새로고침 제외)
  useEffect(() => {
    devLog("탭 변경:", { 
      selectedTab, 
      termsDataLength: Array.isArray(termsData) ? termsData.length : 0,
      isInitialLoad
    });
    
    // 초기 로딩 중이거나 데이터가 없으면 업데이트하지 않음
    if (isInitialLoad || !Array.isArray(termsData) || termsData.length === 0) {
      devLog("초기 로딩 중이거나 데이터가 없으므로 컨텐츠 업데이트 건너뜀");
      return;
    }
    
    const tabIndex = TAB_INDEX_MAP[selectedTab];
    const selectedTerm = termsData.find((term) => Number(term.index) === tabIndex);
    devLog("탭 변경으로 인한 약관 선택:", selectedTerm);
    
    if (selectedTerm) {
      devLog("탭 변경 - 약관 내용 설정:", selectedTerm.content);
      setContent(selectedTerm.content);
      setSelectedTermsIndex(selectedTerm.index);
    } else {
      devLog("탭 변경 - 해당 인덱스의 약관이 없음");
      setContent("");
      setSelectedTermsIndex(tabIndex);
    }
  }, [selectedTab, termsData, isInitialLoad]);

  return (
    <TermsPageContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <TabContainer>
        <TabButton $isActive={selectedTab === "이용약관(KR)"} onClick={() => handleTabClick("이용약관(KR)")}>
          이용약관(KR)
        </TabButton>
        <TabButton $isActive={selectedTab === "이용약관(EN)"} onClick={() => handleTabClick("이용약관(EN)")}>
          이용약관(EN)
        </TabButton>
        <TabButton
          $isActive={selectedTab === "개인정보 취급방침(KR)"}
          onClick={() => handleTabClick("개인정보 취급방침(KR)")}>
          개인정보 취급방침(KR)
        </TabButton>
        <TabButton
          $isActive={selectedTab === "개인정보 취급방침(EN)"}
          onClick={() => handleTabClick("개인정보 취급방침(EN)")}>
          개인정보 취급방침(EN)
        </TabButton>
        <TabButton $isActive={selectedTab === "사업자 정보(KR)"} onClick={() => handleTabClick("사업자 정보(KR)")}>
          사업자 정보(KR)
        </TabButton>
        <TabButton $isActive={selectedTab === "사업자 정보(EN)"} onClick={() => handleTabClick("사업자 정보(EN)")}>
          사업자 정보(EN)
        </TabButton>
        <ButtonContainer>
          <SaveButton onClick={handleSave} disabled={!selectedTermsIndex || isLoading}>
            {isLoading ? "저장 중..." : "저장하기"}
          </SaveButton>
        </ButtonContainer>
      </TabContainer>

      <EditorContainer>
        {isLoading || isInitialLoad ? (
          <LoadingMessage>로딩 중...</LoadingMessage>
        ) : (
          <EditorWrapper>
            <CustomTiptapEditor
            enableImageUpload={false}
              key={`${selectedTermsIndex}-${selectedTab}`} 
              initialContent={content} 
              onEditorUpdate={handleEditorUpdate}
              height={700} // 에디터 높이를 600px로 설정
            />
          </EditorWrapper>
        )}
      </EditorContainer>
    </TermsPageContainer>
  );
};

const TermsPageContainer = styled.div`
  min-width: 1200px;
  width: 100%;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  border-bottom: 1px solid #ddd;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  padding: 10px 16px;
  border: none;
  border-radius: 0;
  background-color: transparent;
  color: ${({ $isActive }) => ($isActive ? "#000000" : "#b6b6b6")};
  font-size: 13px;
  font-weight: ${({ $isActive }) => ($isActive ? "bold" : "normal")};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: ${({ $isActive }) => ($isActive ? "2px solid #000000" : "none")};
  white-space: nowrap;

  &:hover {
    color: ${({ $isActive }) => ($isActive ? "#000000" : "#666666")};
  }
`;

const EditorContainer = styled.div`
  background-color: transparent;
  border-radius: 8px;
  //   padding: 24px;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const EditorWrapper = styled.div`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #333333;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #6c757d;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 20px;
`;

export default TermsPage;
